import React, { useState, ReactNode } from 'react';
import cx from 'classnames';

import { hasAcceptedFileType } from '@trqk/utils';

import './file-drop-zone.css';

/**
 * @typedef {object} FileDropZoneRowProps
 * @property {string} addClasses optional class names to add to the div for the entire component
 * @property {number} ariaRowIndex index of the row
 * @property {ReactNode} children contents of this row
 * @property {ApolloClient} client
 * @property {FileTypeKey[]} acceptFileTypes a list of accepted FileType keys
 * @property {function} handleDrop a callback invoked for the list of files that were dropped
 * @property {object} rowProps the return value of a method on the the react-table row called
 * row.getRowProps()
 */
interface FileDropZoneRowProps {
    addClasses?: string;
    ariaRowIndex: number;
    children: ReactNode;
    acceptFileTypes: FileTypeKey[];
    handleDrop: HandleDrop;
    rowProps: Record<string, unknown>;
}

/**
 * A variation of a FileDropZone that is set up as a table row. Since it's being used in a
 * react-window table, we are using a `<div>` as the container.
 * Allows a user to drag and drop one or more files and trigger a callback.
 * Note that all elements in contained in the dropzone will have their mouse events disabled unless
 * you give them the `respond-to-mouse` class.
 * @param {FileDropZoneRowProps}
 */
export const FileDropZoneRow = ({
    addClasses = '',
    ariaRowIndex,
    children,
    acceptFileTypes,
    handleDrop,
    rowProps,
}: FileDropZoneRowProps): JSX.Element => {
    const [isDragActive, setIsDragActive] = useState(false);
    // eslint-disable-next-line
    const [dragEnterTarget, setDragEnterTarget] = useState<(EventTarget & Element) | null>(null);
    const evenRow = ariaRowIndex % 2 === 0;
    const classNames = cx(
        'file-drop-zone',
        'tr',
        'relative',
        'flex',
        'items-center',
        addClasses,
        {
            'drag-active': isDragActive,
            'even-row': evenRow,
        },
    );

    return (
        <div
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...rowProps}
            aria-rowindex={ariaRowIndex}
            className={classNames}
            onDragEnter={(e: React.DragEvent): void => {
                e.preventDefault();
                e.stopPropagation();

                if (dragEnterTarget === null) {
                    setDragEnterTarget(e.currentTarget);
                    setIsDragActive(true);
                }
            }}
            onDragOver={(e: React.DragEvent): void => {
                e.preventDefault();
            }}
            onDrop={(e: React.DragEvent): void => {
                e.preventDefault();
                e.stopPropagation();
                setIsDragActive(false);
                setDragEnterTarget(null);

                if (e.dataTransfer.files) {
                    const validFiles = [];
                    const invalidFiles = [];

                    for (let i = 0; i < e.dataTransfer.files.length; i += 1) {
                        const file = e.dataTransfer.files[i];

                        if (hasAcceptedFileType(file, acceptFileTypes)) {
                            validFiles.push(file);
                        } else {
                            invalidFiles.push(file);
                        }
                    }

                    handleDrop({
                        validFiles,
                        invalidFiles,
                    });
                }
            }}
            onDragLeave={(e: React.DragEvent): void => {
                e.preventDefault();
                // If there is an element with the FileDropZone that is responding to mouse events
                // with the 'respond-to-mouse' class, it will trigger onDragLeave. In that case, the
                // element that triggered onDragLeave is identified by e.relatedTarget.
                if (
                    // Make sure we are not responding to an onDragLeave caused by dragging over an
                    // element within the FileDropZone element that is responding to pointer events
                    e.relatedTarget === null ||
                    (dragEnterTarget !== null &&
                        !dragEnterTarget.contains(e.relatedTarget as Node))
                ) {
                    setIsDragActive(false);
                    setDragEnterTarget(null);
                }
            }}
        >
            {children}
        </div>
    );
};
